import { authApi } from '@/config/api';

/**
 * HTTP-слой авторизации. Контракт описан в BACKEND_APIS.md.
 * Поток: /attempts (телефон) -> /attempts/{id} (код) -> пара JWT -> /tokens/refresh, /tokens/logout.
 */

export type AuthErrorCode =
  | 'INVALID_PHONE'
  | 'INVALID_CODE_FORMAT'
  | 'CODE_INVALID'
  | 'ATTEMPTS_EXCEEDED'
  | 'RESEND_TOO_SOON'
  | 'ALREADY_CONFIRMED'
  | 'ATTEMPT_NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'UNAVAILABLE'
  | 'INTERNAL';

export class AuthApiError extends Error {
  readonly code: AuthErrorCode;
  readonly status: number;

  constructor(code: AuthErrorCode, status: number, message?: string) {
    super(message ?? code);
    this.name = 'AuthApiError';
    this.code = code;
    this.status = status;
  }
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
}

interface ErrorBody {
  error?: { type?: string; details?: { loc?: string[] }[] };
  detail?: string;
}

/** Сервер отдаёт validation_error с loc — по нему различаем телефон и код. */
function mapValidationError(body: ErrorBody, status: number): AuthApiError {
  const loc = body.error?.details?.[0]?.loc ?? [];
  if (loc.includes('sms_code')) {
    return new AuthApiError('INVALID_CODE_FORMAT', status);
  }
  return new AuthApiError('INVALID_PHONE', status);
}

function mapError(status: number, body: ErrorBody): AuthApiError {
  const type = body.error?.type;

  switch (type) {
    case 'validation_error':
      return mapValidationError(body, status);
    case 'invalid_json':
    case 'invalid_request_body':
      return new AuthApiError('INTERNAL', status);
    case 'sms_code_invalid':
      return new AuthApiError('CODE_INVALID', status);
    case 'sms_code_attempts_exceeded':
      return new AuthApiError('ATTEMPTS_EXCEEDED', status);
    case 'sms_code_resend_too_soon':
      return new AuthApiError('RESEND_TOO_SOON', status);
    case 'sms_code_already_confirmed':
      return new AuthApiError('ALREADY_CONFIRMED', status);
    case 'user_not_found':
      return new AuthApiError('ATTEMPT_NOT_FOUND', status);
    default:
      break;
  }

  if (status === 401) return new AuthApiError('UNAUTHORIZED', status, body.detail);
  if (status === 404) return new AuthApiError('ATTEMPT_NOT_FOUND', status);
  if (status >= 500) return new AuthApiError('INTERNAL', status);
  return new AuthApiError('INTERNAL', status);
}

async function post<T>(path: string, payload: unknown): Promise<{ status: number; data: T | null }> {
  let response: Response;

  try {
    response = await fetch(authApi.url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new AuthApiError('UNAVAILABLE', 0);
  }

  if (response.status === 204) {
    return { status: 204, data: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    throw mapError(response.status, (body ?? {}) as ErrorBody);
  }

  return { status: response.status, data: body as T };
}

/** Запросить SMS-код. Возвращает id попытки для последующего подтверждения. */
export async function requestSmsCode(phoneNumber: string): Promise<string> {
  const { data } = await post<{ item_id: string }>(authApi.endpoints.requestSmsCode, {
    phone_number: phoneNumber,
  });

  if (!data?.item_id) {
    throw new AuthApiError('INTERNAL', 200);
  }

  return data.item_id;
}

/** Подтвердить код и получить пару токенов. */
export async function confirmSmsCode(attemptId: string, smsCode: string): Promise<TokenPair> {
  const { data } = await post<{ access_token: string; refresh_token: string }>(
    authApi.endpoints.confirmSmsCode(attemptId),
    { sms_code: smsCode },
  );

  if (!data?.access_token || !data?.refresh_token) {
    throw new AuthApiError('INTERNAL', 200);
  }

  return { accessToken: data.access_token, refreshToken: data.refresh_token };
}

/** Обновить пару токенов. Сервер ротирует refresh — предыдущий сразу гасится. */
export async function refreshTokens(refreshToken: string): Promise<TokenPair> {
  const { data } = await post<{ access_token: string; refresh_token: string }>(
    authApi.endpoints.refreshTokens,
    { refresh_token: refreshToken },
  );

  if (!data?.access_token || !data?.refresh_token) {
    throw new AuthApiError('INTERNAL', 200);
  }

  return { accessToken: data.access_token, refreshToken: data.refresh_token };
}

/** Отозвать refresh-токен. Идемпотентно. */
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  await post<null>(authApi.endpoints.logout, { refresh_token: refreshToken });
}
